import React, { useMemo, useState, useEffect } from 'react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

function defaultAccessor(row, key) {
  return row?.[key];
}

function getSortValue(row, col) {
  const accessor = col.accessor || ((r) => defaultAccessor(r, col.key));
  return accessor(row);
}

function compareValues(a, b) {
  if (a instanceof Date && b instanceof Date) return a - b;
  const aNum =
    typeof a === 'number'
      ? a
      : typeof a === 'string' && !isNaN(a)
      ? Number(a)
      : null;
  const bNum =
    typeof b === 'number'
      ? b
      : typeof b === 'string' && !isNaN(b)
      ? Number(b)
      : null;
  if (aNum !== null && bNum !== null) return aNum - bNum;

  const sa = (a ?? '').toString().toLowerCase();
  const sb = (b ?? '').toString().toLowerCase();
  if (sa < sb) return -1;
  if (sa > sb) return 1;
  return 0;
}

export default function Table({
  columns = [],
  data = [],
  keyField = 'id',
  size = 'md',
  striped = true,
  hoverable = true,
  bordered = false,
  stickyHeader = true,
  className,
  style,
  defaultSort = null,
  sort = null,
  onSortChange,
  pageSize = 10,
  defaultPage = 1,
  page,
  onPageChange,
  selection = 'none',
  selectedKeys,
  onSelectedKeysChange,
  loading = false,
  emptyContent = 'No data to display.',
  onRowClick,
  header,
}) {
  const [internalSort, setInternalSort] = useState(defaultSort);
  const sortState = sort ?? internalSort;

  const handleRequestSort = (col) => {
    if (!col.sortable) return;
    const next =
      sortState?.key === col.key
        ? { key: col.key, direction: sortState.direction === 'asc' ? 'desc' : 'asc' }
        : { key: col.key, direction: 'asc' };

    if (onSortChange) onSortChange(next);
    else setInternalSort(next);
  };

  const [internalPage, setInternalPage] = useState(defaultPage);
  const currentPage = page ?? internalPage;
  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) {
      const next = totalPages;
      if (onPageChange) onPageChange(next);
      else setInternalPage(next);
    }
  }, [currentPage, totalPages, onPageChange]);

  function goToPage(p) {
    const next = Math.min(Math.max(1, p), totalPages);
    if (onPageChange) onPageChange(next);
    else setInternalPage(next);
  }

  // Selection
  const [internalSelected, setInternalSelected] = useState(new Set());
  const controlled = selectedKeys != null;
  const selectedSet = useMemo(() => {
    if (controlled) {
      const s = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys);
      return s;
    }
    return internalSelected;
  }, [controlled, selectedKeys, internalSelected]);

  const toggleRow = (rowKey) => {
    if (selection === 'none') return;
    if (selection === 'single') {
      const next = new Set(selectedSet.has(rowKey) ? [] : [rowKey]);
      emitSelection(next);
    } else {
      const next = new Set(selectedSet);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      emitSelection(next);
    }
  };

  const pageRowKeys = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end).map((r) => r?.[keyField]);
  }, [data, currentPage, pageSize, keyField]);

  const allPageSelected =
    selection !== 'none' &&
    pageRowKeys.length > 0 &&
    pageRowKeys.every((k) => selectedSet.has(k));
  const somePageSelected =
    selection !== 'none' && pageRowKeys.some((k) => selectedSet.has(k));

  const toggleSelectAllOnPage = () => {
    if (selection !== 'multi') return;
    const next = new Set(selectedSet);
    if (allPageSelected) {
      pageRowKeys.forEach((k) => next.delete(k));
    } else {
      pageRowKeys.forEach((k) => next.add(k));
    }
    emitSelection(next);
  };

  function emitSelection(nextSet) {
    if (onSelectedKeysChange) onSelectedKeysChange(nextSet);
    else setInternalSelected(nextSet);
  }

  // Auto-generate columns from data keys when defined columns don't match the data.
  // This makes the table adaptive when SQL agent returns different fields than the LLM assumed.
  const effectiveColumns = useMemo(() => {
    if (data.length === 0) return columns;
    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') return columns;
    const dataKeys = new Set(Object.keys(firstRow));
    const hasMatch = columns.length > 0 && columns.some((c) => dataKeys.has(c.key));
    if (hasMatch) return columns;
    return Object.keys(firstRow).map((key) => ({
      key,
      header: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      sortable: true,
    }));
  }, [columns, data]);

  const sortedData = useMemo(() => {
    if (!sortState?.key) return data;
    const col = effectiveColumns.find((c) => c.key === sortState.key);
    if (!col) return data;
    const direction = sortState.direction === 'asc' ? 1 : -1;

    return [...data].sort((ra, rb) => {
      const va = getSortValue(ra, col);
      const vb = getSortValue(rb, col);
      return compareValues(va, vb) * direction;
    });
  }, [data, effectiveColumns, sortState]);

  const pageData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, pageSize]);

  const SIZE = {
    sm: { cell: 'px-3 py-2 text-sm', checkbox: 'h-4 w-4' },
    md: { cell: 'px-4 py-2.5 text-sm', checkbox: 'h-4 w-4' },
    lg: { cell: 'px-5 py-3 text-base', checkbox: 'h-5 w-5' },
  };
  const sizeCfg = SIZE[size] || SIZE.md;

  const tableBase = 'min-w-full border-collapse table-auto';
  const thBase =
    'font-semibold text-xs uppercase tracking-wider text-gray-500 ' +
    'bg-gradient-to-b from-gray-50 to-gray-100/80';
  const tdBase = 'text-gray-800';
  const sticky = stickyHeader ? 'sticky top-0 z-10' : '';

  return (
    <div className={cn('w-full', className)} style={style}>
      {header && (
        <div className="mb-3 flex items-center justify-between gap-2">
          {header}
        </div>
      )}

      <div className={cn(
        'overflow-x-auto rounded-xl ring-1 ring-black/[0.05] shadow-sm',
        bordered ? 'border border-gray-200' : ''
      )}>
        <table className={tableBase} role="table">
          <thead>
            <tr role="row" className="border-b border-gray-200/80">
              {selection !== 'none' && (
                <th
                  scope="col"
                  className={cn(thBase, sizeCfg.cell, sticky, 'text-left w-10')}
                >
                  {selection === 'multi' ? (
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        className={cn('rounded border-gray-300', sizeCfg.checkbox)}
                        checked={allPageSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = !allPageSelected && somePageSelected;
                        }}
                        onChange={toggleSelectAllOnPage}
                        aria-label="Select all rows on this page"
                      />
                    </label>
                  ) : null}
                </th>
              )}
              {effectiveColumns.map((col) => {
                const align =
                  col.align === 'right' ? 'text-right' :
                  col.align === 'center' ? 'text-center' : 'text-left';
                const widthStyle = col.width != null ? { width: col.width } : undefined;
                const isActive = sortState?.key === col.key;
                const direction = isActive ? sortState.direction : null;

                const ariaSort =
                  col.sortable
                    ? (isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none')
                    : undefined;

                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={widthStyle}
                    className={cn(
                      thBase,
                      sizeCfg.cell,
                      sticky,
                      align,
                      'whitespace-nowrap select-none',
                      col.headerClassName
                    )}
                    aria-sort={ariaSort}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleRequestSort(col)}
                        className="inline-flex items-center gap-1 hover:text-gray-900"
                        aria-label={`Sort by ${typeof col.header === 'string' ? col.header : col.key}`}
                      >
                        <span>{col.header}</span>
                        <SortIcon active={isActive} direction={direction} />
                      </button>
                    ) : (
                      <span>{col.header}</span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className={cn(striped ? '[&>tr:nth-child(odd)]:bg-white [&>tr:nth-child(even)]:bg-gray-50/60' : '')}>
            {loading ? (
              <tr>
                <td
                  className={cn(sizeCfg.cell, tdBase)}
                  colSpan={(selection !== 'none' ? 1 : 0) + effectiveColumns.length}
                >
                  <div className="flex items-center justify-center gap-2 py-6 text-gray-400 text-sm">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'btn-spin 0.7s linear infinite' }} aria-hidden="true">
                      <style>{`@keyframes btn-spin { to { transform: rotate(360deg); } }`}</style>
                      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2.5" />
                      <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                    Loading…
                  </div>
                </td>
              </tr>
            ) : pageData.length === 0 ? (
              <tr>
                <td
                  className={cn(sizeCfg.cell, tdBase, 'text-center text-sm text-gray-400 py-10')}
                  colSpan={(selection !== 'none' ? 1 : 0) + effectiveColumns.length}
                >
                  {emptyContent}
                </td>
              </tr>
            ) : (
              pageData.map((row, idx) => {
                const rowKey = row?.[keyField] ?? `${currentPage}-${idx}`;
                const isSelected = selectedSet.has(rowKey);
                return (
                  <tr
                    key={rowKey}
                    role="row"
                    className={cn(
                      'border-b border-gray-100 last:border-0 transition-colors duration-100',
                      hoverable ? 'hover:bg-blue-50/40 cursor-default' : '',
                      isSelected ? 'bg-blue-50/70' : '',
                      bordered ? 'border-b border-gray-200' : '',
                      onRowClick ? 'cursor-pointer' : ''
                    )}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {selection !== 'none' && (
                      <td className={cn(sizeCfg.cell, tdBase)}>
                        {selection === 'single' ? (
                          <input
                            type="radio"
                            name="row-select"
                            className={cn('text-blue-600', sizeCfg.checkbox)}
                            checked={isSelected}
                            onChange={() => toggleRow(rowKey)}
                            aria-label="Select row"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <input
                            type="checkbox"
                            className={cn('rounded border-gray-300 text-blue-600', sizeCfg.checkbox)}
                            checked={isSelected}
                            onChange={() => toggleRow(rowKey)}
                            aria-label="Select row"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </td>
                    )}

                    {effectiveColumns.map((col) => {
                      const align =
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                          ? 'text-center'
                          : 'text-left';
                      const value = getSortValue(row, col);
                      const rawContent = col.render ? col.render(value, row) : value ?? '';
                      const content = rawContent !== null && typeof rawContent === 'object' && !Array.isArray(rawContent)
                        ? JSON.stringify(rawContent)
                        : rawContent;

                      return (
                        <td key={col.key} className={cn(sizeCfg.cell, tdBase, align, 'whitespace-nowrap', col.className)}>
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-sm text-gray-500 px-1">
        <div>
          Showing{' '}
          <span className="font-semibold text-gray-700">
            {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1}
          </span>{' '}
          –{' '}
          <span className="font-semibold text-gray-700">
            {Math.min(currentPage * pageSize, totalItems)}
          </span>{' '}
          of <span className="font-semibold text-gray-700">{totalItems}</span>
        </div>

        <div className="inline-flex items-center gap-1.5">
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 text-xs font-medium shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            ← Prev
          </button>
          <span className="px-2 text-xs text-gray-500">
            <span className="font-semibold text-gray-700">{currentPage}</span> / {totalPages}
          </span>
          <button
            type="button"
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 text-xs font-medium shadow-sm hover:bg-gray-50 hover:border-gray-300 transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}

function SortIcon({ active, direction }) {
  const base = 'h-4 w-4 text-gray-400';
  if (!active) {
    return (
      <svg viewBox="0 0 20 20" className={base} aria-hidden="true">
        <path d="M7 7h6M5 10h10M3 13h14" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  return direction === 'asc' ? (
    <svg viewBox="0 0 20 20" className={base} aria-hidden="true">
      <path d="M10 5l4 5H6l4-5z" fill="currentColor" />
    </svg>
  ) : (
    <svg viewBox="0 0 20 20" className={base} aria-hidden="true">
      <path d="M10 15l-4-5h8l-4 5z" fill="currentColor" />
    </svg>
  );
}